import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Plus, Trash2, Check, Save, Loader2,
  FileUp, Shield, Scale,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { legalAPI } from '@/lib/api';
import { cn } from '@/lib/utils';

const SCAN_METHODS = [
  { value: 'keyword_search', label: 'Keyword Search', desc: 'Search website for specific text' },
  { value: 'element_check', label: 'Element Check', desc: 'Check if specific HTML elements exist' },
  { value: 'api_check', label: 'API Check', desc: 'Call a third-party API' },
  { value: 'port_scan', label: 'Port Scan', desc: 'Check specific ports' },
  { value: 'external_db_lookup', label: 'External Database Lookup', desc: 'Check corporate registries' },
  { value: 'content_analysis', label: 'Content Analysis', desc: 'Analyze page content for compliance' },
];

const FINE_FREQUENCIES = [
  { value: 'per_incident', label: 'Per Incident' },
  { value: 'per_day', label: 'Per Day' },
  { value: 'per_record', label: 'Per Record Affected' },
  { value: 'flat_fee', label: 'Flat Fee' },
];

const emptyParam = () => ({
  parameter_name: '', is_externally_scannable: true, scan_method: 'keyword_search',
  scan_config: { keywords: [], match_type: 'contains', css_selectors: [], ports: [], fields_to_verify: [], compliance_indicators: [] },
  compliance_criteria: '', weight: 1.0,
});

const emptyFine = () => ({
  violation_description: '', min_amount: 0, max_amount: 0, frequency: 'per_incident', currency: 'USD',
});

export default function LawWizard({ initialData = null, editMode = false, editLawId = null }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [themes, setThemes] = useState([]);

  // Step 1
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [jurisdiction, setJurisdiction] = useState(initialData?.jurisdiction || '');
  const [selectedCategories, setSelectedCategories] = useState(
    initialData?.categories?.length ? initialData.categories : (initialData?.category ? [initialData.category] : [])
  );
  const [selectedSectorIds, setSelectedSectorIds] = useState(initialData?.sector_ids || []);
  const [selectedThemeIds, setSelectedThemeIds] = useState(initialData?.theme_ids || []);
  const [sourceUrl, setSourceUrl] = useState(initialData?.source_url || '');
  const [scanMethodology, setScanMethodology] = useState(initialData?.scan_methodology || 'website_content_scan');
  const [pdfFile, setPdfFile] = useState(null);

  // Step 2
  const [parameters, setParameters] = useState(
    initialData?.parameters?.length
      ? initialData.parameters.map(p => ({
          parameter_name: p.parameter_name || '',
          is_externally_scannable: p.is_externally_scannable !== false,
          scan_method: p.scan_method || 'keyword_search',
          scan_config: {
            keywords: p.scan_config?.keywords || [],
            match_type: p.scan_config?.match_type || 'contains',
            css_selectors: p.scan_config?.css_selectors || [],
            ports: p.scan_config?.ports || [],
            fields_to_verify: p.scan_config?.fields_to_verify || [],
            compliance_indicators: p.scan_config?.compliance_indicators || [],
            api_endpoint: p.scan_config?.api_endpoint || '',
            content_requirements: p.scan_config?.content_requirements || '',
            data_source: p.scan_config?.data_source || '',
            ...(p.scan_config || {}),
          },
          compliance_criteria: p.compliance_criteria || '',
          weight: p.weight ?? 1.0,
        }))
      : [emptyParam()]
  );

  // Step 3
  const [hasFines, setHasFines] = useState(initialData?.has_fines || (initialData?.potential_fines?.length > 0) || false);
  const [fines, setFines] = useState(
    initialData?.potential_fines?.length
      ? initialData.potential_fines.map(f => ({
          violation_description: f.violation_description || '',
          min_amount: f.min_amount || 0,
          max_amount: f.max_amount || 0,
          frequency: f.frequency || 'per_incident',
          currency: f.currency || 'USD',
        }))
      : [emptyFine()]
  );

  useEffect(() => {
    legalAPI.listCategories(false).then(res => setCategories(res.data.categories || [])).catch(() => {});
    legalAPI.listSectors(true).then(res => setSectors(res.data.sectors || [])).catch(() => {});
    legalAPI.listThemes().then(res => setThemes(res.data.themes || [])).catch(() => {});
  }, []);

  const addParameter = () => setParameters([...parameters, emptyParam()]);
  const removeParameter = (i) => setParameters(parameters.filter((_, idx) => idx !== i));
  const updateParameter = (i, field, value) => {
    const updated = [...parameters];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updated[i] = { ...updated[i], [parent]: { ...updated[i][parent], [child]: value } };
    } else {
      updated[i] = { ...updated[i], [field]: value };
    }
    setParameters(updated);
  };

  const addFine = () => setFines([...fines, emptyFine()]);
  const removeFine = (i) => setFines(fines.filter((_, idx) => idx !== i));
  const updateFine = (i, field, value) => {
    const updated = [...fines];
    updated[i] = { ...updated[i], [field]: value };
    setFines(updated);
  };

  const handleSave = async (asApproved = false) => {
    if (!title.trim()) { toast.error('Law title is required'); return; }
    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        jurisdiction: jurisdiction.trim(),
        category: selectedCategories[0] || '',
        categories: selectedCategories,
        sector_ids: selectedSectorIds,
        theme_ids: selectedThemeIds,
        source_url: sourceUrl.trim(),
        scan_methodology: scanMethodology,
        parameters: parameters.filter(p => p.parameter_name.trim()),
        has_fines: hasFines,
        potential_fines: hasFines ? fines.filter(f => f.violation_description.trim()) : [],
        status: asApproved ? 'approved' : 'draft',
      };

      let lawId = editLawId;

      if (editMode && editLawId) {
        // Update existing law
        await legalAPI.updateLaw(editLawId, payload);
        if (asApproved) {
          await legalAPI.approveLaw(editLawId);
        }
      } else {
        // Create new law
        const res = await legalAPI.createLaw(payload);
        lawId = res.data.law?.law_id;
      }

      // Upload PDF if provided
      if (pdfFile && lawId) {
        try {
          await legalAPI.uploadPdf(lawId, pdfFile);
        } catch { toast.error('Law saved but PDF upload failed'); }
      }

      toast.success(editMode ? 'Law updated' : (asApproved ? 'Law approved & activated!' : 'Law saved as draft'));
      navigate(editMode ? `/laws/${editLawId}` : '/laws');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save law');
    } finally { setLoading(false); }
  };

  const stepTitles = { 1: 'Basic Information', 2: 'Scannable Parameters', 3: 'Fine Schedule', 4: 'Review & Save' };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="law-wizard">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(editMode ? `/laws/${editLawId}` : '/laws')} data-testid="law-wizard-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{editMode ? 'Edit Law' : 'Add New Law'}</h1>
          <p className="text-sm text-muted-foreground">Step {step} of 4: {stepTitles[step]}</p>
        </div>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-1.5">
        {[1,2,3,4].map(s => (
          <div key={s} className={cn('h-2 rounded-full transition-all duration-300', s === step ? 'w-10 bg-primary' : s < step ? 'w-3 bg-primary/60' : 'w-3 bg-border')} />
        ))}
      </div>

      <Card className="border-border/60">
        <CardContent className="p-6">

          {/* STEP 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Law Title *</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Ley de Notificaci\u00f3n de Pol\u00edtica de Privacidad" data-testid="law-title-input" className="bg-muted/30 border-border" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Jurisdiction</Label>
                  <Input value={jurisdiction} onChange={e => setJurisdiction(e.target.value)} placeholder="e.g., Puerto Rico" data-testid="law-jurisdiction-input" className="bg-muted/30 border-border" />
                </div>
                <div className="space-y-2">
                  <Label>Categories</Label>
                  <div className="flex flex-wrap gap-2" data-testid="law-category-select">
                    {categories.map(c => {
                      const isSelected = selectedCategories.includes(c.title);
                      return (
                        <button key={c.category_id} type="button" onClick={() => {
                          setSelectedCategories(prev => isSelected ? prev.filter(x => x !== c.title) : [...prev, c.title]);
                        }} className={cn(
                          'rounded-lg border px-3 py-1.5 text-xs font-medium transition-[background-color,border-color] duration-200',
                          isSelected ? 'bg-accent/40 border-primary/50 text-foreground' : 'border-border/60 text-muted-foreground hover:border-border hover:bg-muted/20'
                        )}>
                          {c.title}
                        </button>
                      );
                    })}
                  </div>
                  {selectedCategories.length > 0 && (
                    <div className="flex flex-wrap gap-1">{selectedCategories.map(c => <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>)}</div>
                  )}
                </div>
              </div>

              {/* Sectors */}
              <div className="space-y-2">
                <Label>Applicable Sectors *</Label>
                <div className="flex flex-wrap gap-2" data-testid="law-sectors-select">
                  {sectors.map(s => {
                    const isSelected = selectedSectorIds.includes(s.id);
                    return (
                      <button key={s.id} type="button" onClick={() => {
                        setSelectedSectorIds(prev => isSelected ? prev.filter(x => x !== s.id) : [...prev, s.id]);
                      }} className={cn(
                        'rounded-lg border px-3 py-1.5 text-xs font-medium transition-[background-color,border-color] duration-200',
                        isSelected ? 'bg-accent/40 border-primary/50 text-foreground' : 'border-border/60 text-muted-foreground hover:border-border hover:bg-muted/20'
                      )}>
                        {s.name}{s.is_universal && ' *'}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Themes */}
              <div className="space-y-2">
                <Label>Themes *</Label>
                <div className="flex flex-wrap gap-2" data-testid="law-themes-select">
                  {themes.map(th => {
                    const isSelected = selectedThemeIds.includes(th.id);
                    return (
                      <button key={th.id} type="button" onClick={() => {
                        setSelectedThemeIds(prev => isSelected ? prev.filter(x => x !== th.id) : [...prev, th.id]);
                      }} className={cn(
                        'rounded-lg border px-3 py-1.5 text-xs font-medium transition-[background-color,border-color] duration-200',
                        isSelected ? 'bg-accent/40 border-primary/50 text-foreground' : 'border-border/60 text-muted-foreground hover:border-border hover:bg-muted/20'
                      )}>
                        {th.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description / Summary</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Summary of what this law requires..." className="bg-muted/30 border-border min-h-[100px]" data-testid="law-description-input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source URL</Label>
                  <Input value={sourceUrl} onChange={e => setSourceUrl(e.target.value)} placeholder="https://..." data-testid="law-source-url-input" className="bg-muted/30 border-border" />
                </div>
                <div className="space-y-2">
                  <Label>Upload PDF (optional)</Label>
                  <Input type="file" accept=".pdf" onChange={e => setPdfFile(e.target.files?.[0] || null)} data-testid="law-pdf-upload" className="bg-muted/30 border-border" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Parameters */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Define what the scanner should check. Each parameter is a specific compliance rule.</p>
                <Button variant="outline" size="sm" onClick={addParameter} data-testid="add-parameter-button">
                  <Plus className="mr-1 h-3 w-3" />Add Parameter
                </Button>
              </div>
              {parameters.map((param, i) => (
                <Card key={i} className="border-border/40 bg-muted/5">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="secondary" className="text-[10px]">Parameter {i + 1}</Badge>
                      {parameters.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeParameter(i)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Parameter Name *</Label>
                        <Input value={param.parameter_name} onChange={e => updateParameter(i, 'parameter_name', e.target.value)} placeholder="e.g., Privacy Policy Present on Website" className="h-9 bg-muted/30 border-border text-sm" data-testid={`param-name-${i}`} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Scan Method</Label>
                        <Select value={param.scan_method} onValueChange={v => updateParameter(i, 'scan_method', v)}>
                          <SelectTrigger className="h-9 bg-muted/30 border-border text-sm" data-testid={`param-method-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{SCAN_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch checked={param.is_externally_scannable} onCheckedChange={v => updateParameter(i, 'is_externally_scannable', v)} data-testid={`param-scannable-${i}`} />
                      <Label className="text-xs">{param.is_externally_scannable ? 'Externally scannable' : 'Requires internal audit'}</Label>
                      <div className="ml-auto flex items-center gap-2">
                        <Label className="text-xs text-muted-foreground">Weight</Label>
                        <Input type="number" step="0.1" value={param.weight} onChange={e => updateParameter(i, 'weight', parseFloat(e.target.value) || 1)} className="h-8 w-20 bg-muted/30 border-border text-sm" />
                      </div>
                    </div>

                    {/* Method-specific config */}
                    {param.scan_method === 'keyword_search' && (
                      <div className="space-y-2 pl-2 border-l-2 border-primary/20">
                        <Label className="text-xs">Keywords (comma-separated)</Label>
                        <Input value={(param.scan_config?.keywords || []).join(', ')} onChange={e => updateParameter(i, 'scan_config.keywords', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="privacy policy, data protection, cookies" className="h-8 bg-muted/30 border-border text-sm" />
                        <Select value={param.scan_config?.match_type || 'contains'} onValueChange={v => updateParameter(i, 'scan_config.match_type', v)}>
                          <SelectTrigger className="h-8 w-[140px] bg-muted/30 border-border text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="exact">Exact match</SelectItem>
                            <SelectItem value="contains">Contains</SelectItem>
                            <SelectItem value="regex">Regex</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {param.scan_method === 'element_check' && (
                      <div className="space-y-2 pl-2 border-l-2 border-primary/20">
                        <Label className="text-xs">CSS Selectors (comma-separated)</Label>
                        <Input value={(param.scan_config?.css_selectors || []).join(', ')} onChange={e => updateParameter(i, 'scan_config.css_selectors', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="a[href*='privacy'], .cookie-banner" className="h-8 bg-muted/30 border-border text-sm" />
                      </div>
                    )}
                    {param.scan_method === 'port_scan' && (
                      <div className="space-y-2 pl-2 border-l-2 border-primary/20">
                        <Label className="text-xs">Ports (comma-separated)</Label>
                        <Input value={(param.scan_config?.ports || []).join(', ')} onChange={e => updateParameter(i, 'scan_config.ports', e.target.value.split(',').map(s => parseInt(s.trim())).filter(Boolean))} placeholder="80, 443, 22" className="h-8 bg-muted/30 border-border text-sm" />
                      </div>
                    )}
                    {param.scan_method === 'api_check' && (
                      <div className="space-y-2 pl-2 border-l-2 border-primary/20">
                        <Label className="text-xs">API Endpoint</Label>
                        <Input value={param.scan_config?.api_endpoint || ''} onChange={e => updateParameter(i, 'scan_config.api_endpoint', e.target.value)} placeholder="https://api.example.com/check" className="h-8 bg-muted/30 border-border text-sm" />
                      </div>
                    )}
                    {(param.scan_method === 'content_analysis' || param.scan_method === 'external_db_lookup') && (
                      <div className="space-y-2 pl-2 border-l-2 border-primary/20">
                        <Label className="text-xs">{param.scan_method === 'content_analysis' ? 'Content Requirements' : 'Data Source'}</Label>
                        <Textarea value={param.scan_config?.content_requirements || param.scan_config?.data_source || ''} onChange={e => updateParameter(i, param.scan_method === 'content_analysis' ? 'scan_config.content_requirements' : 'scan_config.data_source', e.target.value)} className="h-16 bg-muted/30 border-border text-sm" />
                      </div>
                    )}

                    <div className="space-y-1">
                      <Label className="text-xs">Compliance Criteria</Label>
                      <Input value={param.compliance_criteria} onChange={e => updateParameter(i, 'compliance_criteria', e.target.value)} placeholder="What constitutes compliant vs non-compliant" className="h-8 bg-muted/30 border-border text-sm" data-testid={`param-criteria-${i}`} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* STEP 3: Fines */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch checked={hasFines} onCheckedChange={setHasFines} data-testid="has-fines-toggle" />
                <Label>Can this law result in fines?</Label>
              </div>
              {hasFines && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Define potential violations and fine amounts.</p>
                    <Button variant="outline" size="sm" onClick={addFine} data-testid="add-fine-button">
                      <Plus className="mr-1 h-3 w-3" />Add Violation
                    </Button>
                  </div>
                  {fines.map((fine, i) => (
                    <Card key={i} className="border-border/40 bg-muted/5">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="text-[10px]">Violation {i + 1}</Badge>
                          {fines.length > 1 && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeFine(i)}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Violation Description</Label>
                          <Input value={fine.violation_description} onChange={e => updateFine(i, 'violation_description', e.target.value)} placeholder="e.g., Failure to display privacy policy" className="h-9 bg-muted/30 border-border text-sm" data-testid={`fine-desc-${i}`} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Min Amount ($)</Label>
                            <Input type="number" value={fine.min_amount} onChange={e => updateFine(i, 'min_amount', parseFloat(e.target.value) || 0)} className="h-8 bg-muted/30 border-border text-sm" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Max Amount ($)</Label>
                            <Input type="number" value={fine.max_amount} onChange={e => updateFine(i, 'max_amount', parseFloat(e.target.value) || 0)} className="h-8 bg-muted/30 border-border text-sm" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Frequency</Label>
                            <Select value={fine.frequency} onValueChange={v => updateFine(i, 'frequency', v)}>
                              <SelectTrigger className="h-8 bg-muted/30 border-border text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>{FINE_FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Currency</Label>
                            <Input value={fine.currency} onChange={e => updateFine(i, 'currency', e.target.value)} className="h-8 bg-muted/30 border-border text-sm" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 4: Review */}
          {step === 4 && (
            <div className="space-y-4">
              <Card className="border-border/40 bg-muted/5">
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-xs text-muted-foreground">Title</span><p className="font-semibold">{title || '\u2014'}</p></div>
                    <div><span className="text-xs text-muted-foreground">Jurisdiction</span><p className="font-medium">{jurisdiction || '\u2014'}</p></div>
                    <div><span className="text-xs text-muted-foreground">Categories</span><div className="flex flex-wrap gap-1 mt-0.5">{selectedCategories.length > 0 ? selectedCategories.map(c => <Badge key={c} variant="secondary" className="text-[10px]">{c}</Badge>) : <p className="font-medium">{"\u2014"}</p>}</div></div>
                    <div><span className="text-xs text-muted-foreground">Source</span>{sourceUrl ? <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="text-primary text-xs hover:underline">{sourceUrl}</a> : <p>\u2014</p>}</div>
                  </div>
                  {description && <div><span className="text-xs text-muted-foreground">Description</span><p className="text-sm text-foreground/80 mt-1">{description}</p></div>}
                  {pdfFile && <div className="flex items-center gap-2"><FileUp className="h-4 w-4 text-primary" /><span className="text-xs">{pdfFile.name}</span></div>}
                </CardContent>
              </Card>

              <div><span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Parameters ({parameters.filter(p => p.parameter_name.trim()).length})</span></div>
              {parameters.filter(p => p.parameter_name.trim()).map((p, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border border-border/40 px-4 py-2">
                  <Shield className="h-4 w-4 text-primary shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{p.parameter_name}</p>
                    <p className="text-[10px] text-muted-foreground">{SCAN_METHODS.find(m => m.value === p.scan_method)?.label} \u2022 Weight: {p.weight} \u2022 {p.is_externally_scannable ? 'Scannable' : 'Internal audit'}</p>
                  </div>
                </div>
              ))}

              {hasFines && fines.filter(f => f.violation_description.trim()).length > 0 && (
                <>
                  <div><span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Fine Schedule</span></div>
                  {fines.filter(f => f.violation_description.trim()).map((f, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg border border-vrs-red/20 px-4 py-2">
                      <Scale className="h-4 w-4 text-vrs-red shrink-0" />
                      <div>
                        <p className="text-sm">{f.violation_description}</p>
                        <p className="text-[10px] text-muted-foreground">${f.min_amount.toLocaleString()} - ${f.max_amount.toLocaleString()} {f.currency} ({f.frequency.replace('_', ' ')})</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : navigate(editMode ? `/laws/${editLawId}` : '/laws')} className="text-muted-foreground">
          <ArrowLeft className="mr-2 h-4 w-4" />{step > 1 ? 'Back' : 'Cancel'}
        </Button>
        <div className="flex gap-2">
          {step < 4 ? (
            <Button onClick={() => setStep(step + 1)} data-testid="law-wizard-next">
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleSave(false)} disabled={loading} data-testid="law-save-draft">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save as Draft
              </Button>
              <Button onClick={() => handleSave(true)} disabled={loading} data-testid="law-approve">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}Approve & Activate
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
